import React from 'react';
import { Loader2, RefreshCw, Database, Wifi, WifiOff } from 'lucide-react';

// Skeleton loaders for different content types
export const SkeletonCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse ${className}`}>
    <div className="flex items-start justify-between mb-4">
      <div className="flex-1">
        <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
        <div className="h-4 bg-gray-200 rounded w-1/2" />
      </div>
      <div className="h-6 bg-gray-200 rounded w-16" />
    </div>
    <div className="space-y-2">
      <div className="h-3 bg-gray-200 rounded w-full" />
      <div className="h-3 bg-gray-200 rounded w-5/6" />
      <div className="h-3 bg-gray-200 rounded w-4/6" />
    </div>
    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
      <div className="h-4 bg-gray-200 rounded w-20" />
      <div className="h-4 bg-gray-200 rounded w-16" />
    </div>
  </div>
);

export const SkeletonTable: React.FC<{ rows?: number; columns?: number }> = ({ 
  rows = 5, 
  columns = 4 
}) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-pulse">
    <div className="bg-gray-50 border-b border-gray-200 p-4">
      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        {Array.from({ length: columns }).map((_, i) => (
          <div key={i} className="h-4 bg-gray-200 rounded w-3/4" />
        ))}
      </div>
    </div>
    <div className="divide-y divide-gray-200">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="p-4">
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
            {Array.from({ length: columns }).map((_, colIndex) => (
              <div key={colIndex} className="h-4 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const SkeletonList: React.FC<{ items?: number }> = ({ items = 3 }) => (
  <div className="space-y-4">
    {Array.from({ length: items }).map((_, i) => (
      <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
        <div className="flex items-start space-x-3">
          <div className="w-10 h-10 bg-gray-200 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
            <div className="h-3 bg-gray-200 rounded w-full" />
          </div>
          <div className="h-6 bg-gray-200 rounded w-16" />
        </div>
      </div>
    ))}
  </div>
);

// Loading states with context
export const LoadingSpinner: React.FC<{ 
  size?: 'sm' | 'md' | 'lg';
  message?: string;
  className?: string;
}> = ({ 
  size = 'md', 
  message,
  className = '' 
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div className="text-center">
        <Loader2 className={`${sizeClasses[size]} text-blue-600 animate-spin mx-auto`} />
        {message && (
          <p className="text-gray-600 text-sm mt-2">{message}</p>
        )}
      </div>
    </div>
  );
};

export const LoadingOverlay: React.FC<{ 
  isVisible: boolean;
  message?: string;
  progress?: number;
}> = ({ 
  isVisible, 
  message = 'Chargement...',
  progress 
}) => {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
      <div className="bg-white rounded-xl shadow-xl p-8 max-w-sm w-full mx-4">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{message}</h3>
          
          {progress !== undefined && (
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
          
          <p className="text-sm text-gray-600">
            {progress !== undefined ? `${Math.round(progress)}% complété` : 'Veuillez patienter...'}
          </p>
        </div>
      </div>
    </div>
  );
};

// Connection status indicator
export const ConnectionStatus: React.FC = () => {
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);
  const [lastOnline, setLastOnline] = React.useState<Date | null>(null);

  React.useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setLastOnline(new Date());
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (isOnline) return null;

  return (
    <div className="fixed bottom-4 left-4 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50 animate-slide-up">
      <WifiOff className="w-4 h-4" />
      <span className="text-sm font-medium">Hors ligne</span>
      {lastOnline && (
        <span className="text-xs opacity-75">
          Dernière connexion : {lastOnline.toLocaleTimeString()}
        </span>
      )}
    </div>
  );
};

// Database connection status
export const DatabaseStatus: React.FC<{ isConnected: boolean }> = ({ isConnected }) => (
  <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium ${
    isConnected 
      ? 'bg-green-100 text-green-800' 
      : 'bg-orange-100 text-orange-800'
  }`}>
    <Database className="w-3 h-3" />
    <span>{isConnected ? 'Base connectée' : 'Mode hors ligne'}</span>
  </div>
);

// Progressive loading component
export const ProgressiveLoader: React.FC<{
  steps: Array<{ name: string; completed: boolean }>;
  currentStep?: number;
}> = ({ steps, currentStep = 0 }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
    <h3 className="font-semibold text-gray-900 mb-4">Initialisation</h3>
    <div className="space-y-3">
      {steps.map((step, index) => (
        <div key={index} className="flex items-center space-x-3">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
            step.completed 
              ? 'bg-green-600 text-white' 
              : index === currentStep 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-500'
          }`}>
            {step.completed ? (
              '✓'
            ) : index === currentStep ? (
              <RefreshCw className="w-3 h-3 animate-spin" />
            ) : (
              index + 1
            )}
          </div>
          <span className={`text-sm ${
            step.completed ? 'text-green-700' : 'text-gray-600'
          }`}>
            {step.name}
          </span>
        </div>
      ))}
    </div>
  </div>
);